import SettingsSection from '../../SettingsSection';

import {
  CalloutType,
  Expand,
  TextIconColor,
  Typography,
  TypographyFont,
  TypographySize,
  TypographyWeight
} from '~/components/core';
import { Markdown } from '~/components/Markdown';
import { useMarkdownFile } from '~/hooks';

const ApiReference = () => {
  const { markdown: errorHandling } = useMarkdownFile(
    '/docs/ApiSettings/ApiErrorHandling.md'
  );
  const { markdown: apiOverview } = useMarkdownFile(
    '/docs/ApiSettings/ApiOverview.md'
  );
  const { markdown: apiRequest } = useMarkdownFile(
    '/docs/ApiSettings/ApiRequestExample.md'
  );
  const { markdown: apiRequestParameters } = useMarkdownFile(
    '/docs/ApiSettings/ApiRequestParameters.md'
  );
  const { markdown: apiBlockquote } = useMarkdownFile(
    '/docs/ApiSettings/ApiBlockquote.md'
  );

  return (
    <SettingsSection title="API Reference">
      <div
        className="mb-3 mt-6 flex w-full flex-col"
        data-testid="api-reference-container"
      >
        <div
          className="flex w-full flex-col gap-5"
          data-testid="api-reference-available-apis"
        >
          <div className="bg-blue-chat-background w-full rounded-md p-5">
            <Expand
              startExpanded={true}
              trigger={
                <div className="flex w-full justify-between">
                  <Typography
                    color={TextIconColor.PRIMARY}
                    font={TypographyFont.HEADING}
                    size={TypographySize.LARGE}
                    weight={TypographyWeight.SEMI_BOLD}
                  >
                    Agents API
                  </Typography>
                </div>
              }
            >
              <div
                className="mr-6 mt-2 flex flex-col gap-2 pl-6"
                data-testid="agents-api-section"
              >
                <Typography color={TextIconColor.SECONDARY}>
                  Access and manage Kindo agents programmatically through
                  Kindo's Agents API. For complete endpoint documentation, refer
                  to Kindo's Swagger Documentation:{' '}
                  <a
                    className="text-blue-500 hover:underline"
                    href="https://api.kindo.ai/v1/docs/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    https://api.kindo.ai/v1/docs/
                  </a>
                </Typography>
              </div>
            </Expand>
          </div>
          <div className="bg-blue-chat-background w-full rounded-md p-5">
            <Expand
              startExpanded={true}
              trigger={
                <div className="flex w-full justify-between">
                  <Typography
                    color={TextIconColor.PRIMARY}
                    font={TypographyFont.HEADING}
                    size={TypographySize.LARGE}
                    weight={TypographyWeight.SEMI_BOLD}
                  >
                    Completions API
                  </Typography>
                </div>
              }
            >
              <div
                className="mr-6 mt-2 flex flex-col gap-5 pl-6"
                data-testid="completions-api-section"
              >
                <div
                  className="flex w-full flex-col gap-2"
                  data-testid="api-reference-overview"
                >
                  <Markdown textColor={TextIconColor.SECONDARY}>
                    {apiOverview}
                  </Markdown>
                </div>

                <Markdown calloutType={CalloutType.INFO}>
                  {apiBlockquote}
                </Markdown>
                <Markdown
                  styleDocsComponents
                  textColor={TextIconColor.SECONDARY}
                >
                  {apiRequestParameters}
                </Markdown>
                <Markdown styleDocsComponents textColor={TextIconColor.PRIMARY}>
                  {apiRequest}
                </Markdown>

                <div className="mt-6">
                  <Typography
                    color={TextIconColor.PRIMARY}
                    font={TypographyFont.HEADING}
                    size={TypographySize.H4}
                    weight={TypographyWeight.SEMI_BOLD}
                  >
                    Error Handling
                  </Typography>

                  <div className="mt-3">
                    <Typography
                      color={TextIconColor.SECONDARY}
                      size={TypographySize.SMALL}
                    >
                      Kindo API uses standard HTTP status codes to indicate the
                      success or failure of a request. In general, 2xx status
                      codes indicate success, 4xx indicate errors with data
                      provided by the user (e.g. invalid input), and 5xx
                      indicate Kindo server errors.
                      <br />
                      <br />
                      For some 4xx and 5xx responses, Kindo will also return a
                      code and message indicating the error.
                    </Typography>
                  </div>

                  <div className="mt-4">
                    <Markdown
                      styleDocsComponents
                      textColor={TextIconColor.PRIMARY}
                    >
                      {errorHandling}
                    </Markdown>
                  </div>
                </div>
              </div>
            </Expand>
          </div>
        </div>
      </div>
    </SettingsSection>
  );
};

export default ApiReference;
